import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Crown, Check, Zap, Shield, TrendingUp, Gift } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';

const VIP = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const tiers = [
    {
      name: 'Silver',
      price: '$99/month',
      color: 'from-gray-400 to-gray-500',
      features: [
        'Lower trading fees (0.08%)',
        'Priority customer support',
        'Advanced trading tools',
        'Weekly market insights'
      ]
    },
    {
      name: 'Gold',
      price: '$299/month',
      color: 'from-yellow-400 to-yellow-500',
      popular: true,
      features: [
        'Lowest trading fees (0.05%)',
        '24/7 VIP support',
        'All advanced tools',
        'Daily market analysis',
        'Exclusive signals',
        'Early feature access'
      ]
    },
    {
      name: 'Platinum',
      price: '$999/month',
      color: 'from-purple-400 to-purple-500',
      features: [
        'No trading fees (0%)',
        'Dedicated account manager',
        'Premium tools & API',
        'Real-time insights',
        'Private signals',
        'Exclusive events',
        'Higher withdrawal limits'
      ]
    }
  ];

  const benefits = [
    { icon: TrendingUp, title: 'Better Rates', description: 'Lower fees on all trades' },
    { icon: Shield, title: 'Priority Support', description: '24/7 VIP customer service' },
    { icon: Zap, title: 'Advanced Tools', description: 'Access premium features' },
    { icon: Gift, title: 'Exclusive Rewards', description: 'Special bonuses & gifts' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      <div className="sticky top-0 bg-slate-900/95 backdrop-blur-sm border-b border-cyan-500/20 z-10">
        <div className="flex items-center justify-between px-4 py-4">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-cyan-400 hover:text-cyan-300">
            <ArrowLeft className="w-5 h-5" />
            <span className="font-semibold">Back</span>
          </button>
          <h1 className="text-lg font-bold text-cyan-400">VIP Membership</h1>
          <div className="w-16"></div>
        </div>
      </div>

      <div className="px-4 py-6">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-yellow-900/30 to-orange-900/30 border border-yellow-500/30 rounded-xl p-6 mb-6 text-center">
          <Crown className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-yellow-400 mb-2">Become a VIP Member</h2>
          <p className="text-sm text-yellow-600">Unlock exclusive benefits and premium features</p>
        </div>

        {/* Benefits */}
        <div className="mb-6">
          <h3 className="text-base sm:text-lg font-bold text-cyan-400 mb-4">VIP Benefits</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {benefits.map((benefit, idx) => {
              const Icon = benefit.icon;
              return (
                <div key={idx} className="bg-slate-800/50 border border-cyan-500/20 rounded-xl p-4">
                  <Icon className="w-8 h-8 text-cyan-400 mb-2" />
                  <div className="font-semibold text-cyan-300 text-sm mb-1">{benefit.title}</div>
                  <div className="text-xs text-cyan-700">{benefit.description}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Pricing Tiers */}
        <div>
          <h3 className="text-base sm:text-lg font-bold text-cyan-400 mb-4">Choose Your Plan</h3>
          {tiers.map((tier, idx) => (
            <div key={idx} className="bg-slate-800/50 border border-cyan-500/20 rounded-xl p-4 sm:p-5 mb-4 relative">
              {tier.popular && (
                <div className="absolute -top-3 right-4 bg-gradient-to-r from-yellow-400 to-orange-400 text-black text-xs font-bold px-3 py-1 rounded-full">
                  MOST POPULAR
                </div>
              )}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-gradient-to-br ${tier.color} flex items-center justify-center`}>
                    <Crown className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
                  </div>
                  <div>
                    <div className="font-bold text-cyan-300 text-base sm:text-lg">{tier.name}</div>
                    <div className="text-cyan-700 text-sm">{tier.price}</div>
                  </div>
                </div>
              </div>
              <div className="space-y-2 mb-4">
                {tier.features.map((feature, fIdx) => (
                  <div key={fIdx} className="flex items-start gap-2 text-sm text-cyan-600">
                    <Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
              <Button
                onClick={() => toast({ title: 'Coming Soon', description: 'VIP membership will be available soon' })}
                className={`w-full h-11 sm:h-10 bg-gradient-to-r ${tier.color} text-white font-semibold neon-button`}
              >
                Subscribe Now
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default VIP;